import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';
import RetroWaveform from './RetroWaveform';

const StatusItem = ({ label, value, color = retroColors.accent }) => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.625rem 0',
        borderBottom: '1px solid rgba(63, 106, 100, 0.2)',
    }}>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            color: retroColors.muted,
            textTransform: 'uppercase',
        }}>
            {label}
        </Typography>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.85rem',
            color: color,
            fontWeight: 500,
            textShadow: `0 0 8px ${color}`,
        }}>
            {value}
        </Typography>
    </Box>
);

const RetroTelemetry = ({ appConfig, showWaveform = true }) => {
    const [sessionTime, setSessionTime] = useState(0);
    const [apiCalls, setApiCalls] = useState(0);
    const [solverStatus, setSolverStatus] = useState({ status: 'checking', version: null });

    // Session timer
    useEffect(() => {
        const interval = setInterval(() => {
            setSessionTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Solver health check
    useEffect(() => {
        const checkHealth = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || '';
                const res = await fetch(`${apiUrl}/health`);
                if (res.ok) {
                    const data = await res.json();
                    setSolverStatus({
                        status: data.status === 'healthy' ? 'online' : 'degraded',
                        version: data.version || null
                    });
                } else {
                    setSolverStatus({ status: 'offline', version: null });
                }
            } catch (err) {
                console.error('Health check failed:', err);
                setSolverStatus({ status: 'offline', version: null });
            }
        };

        checkHealth();
        const interval = setInterval(checkHealth, 30000);
        return () => clearInterval(interval);
    }, []);

    // Track API calls
    useEffect(() => {
        const savedCalls = parseInt(sessionStorage.getItem('retroApiCalls') || '0');
        setApiCalls(savedCalls);

        const handleApiCall = () => {
            setApiCalls(prev => {
                const newVal = prev + 1;
                sessionStorage.setItem('retroApiCalls', newVal.toString());
                return newVal;
            });
        };
        window.addEventListener('retro-api-call', handleApiCall);
        return () => window.removeEventListener('retro-api-call', handleApiCall);
    }, []);

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Box sx={{
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(63, 106, 100, 0.3)',
            padding: 2,
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                color: appConfig?.color || '#b28c50',
                marginBottom: 2,
                paddingBottom: 1,
                borderBottom: `1px solid ${appConfig?.color || '#b28c50'}40`,
                transition: 'color 0.3s ease, border-color 0.3s ease',
            }}>
                {appConfig?.statusLabel || 'System Status'}
            </Typography>

            {/* Backend Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: solverStatus.status === 'online' ? retroColors.accent :
                        solverStatus.status === 'checking' ? vortxColors.warning :
                            solverStatus.status === 'degraded' ? vortxColors.warning : vortxColors.error,
                    boxShadow: solverStatus.status === 'online' ? `0 0 10px ${retroColors.accent}` :
                        solverStatus.status === 'checking' ? `0 0 10px ${vortxColors.warning}` :
                            solverStatus.status === 'degraded' ? `0 0 10px ${vortxColors.warning}` : `0 0 10px ${vortxColors.error}`,
                    animation: solverStatus.status === 'checking' ? 'none' : 'pulse 2s infinite',
                    '@keyframes pulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                    }
                }} />
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.75rem',
                    color: solverStatus.status === 'online' ? retroColors.accent :
                        solverStatus.status === 'checking' ? vortxColors.warning :
                            solverStatus.status === 'degraded' ? vortxColors.warning : vortxColors.error,
                    textTransform: 'uppercase',
                }}>
                    {solverStatus.status === 'online' ? 'Backend Online' :
                        solverStatus.status === 'checking' ? 'Checking...' :
                            solverStatus.status === 'degraded' ? 'Degraded' : 'Offline'}
                </Typography>
            </Box>
            {solverStatus.version && (
                <Box sx={{ mb: 1, ml: 2 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.dimmed,
                    }}>
                        v{solverStatus.version}
                    </Typography>
                </Box>
            )}

            <StatusItem label="Session" value={formatTime(sessionTime)} />
            <StatusItem label="API Calls" value={apiCalls.toString()} />
            <StatusItem label="Region" value="us-central1" color={retroColors.dimmed} />

            {/* System Activity Waveform */}
            {showWaveform && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.dimmed,
                        textTransform: 'uppercase',
                        mb: 1,
                    }}>
                        System Activity
                    </Typography>
                    <RetroWaveform height={35} />
                </Box>
            )}
        </Box>
    );
};

export default RetroTelemetry;
